<template>
    <BaseField
        :label="label"
        :hint="hint"
        :is-optional="isOptional"
        :name="name"
        :error-messages="errorMessagesTotal"
        :custom-theme-base="customThemeBase"
        data-spec-class="field-select-search"
    >
        <Combobox
            v-slot="{ open }"
            v-model="selectedOption"
            as="div"
            :disabled="isDisabled"
        >
            <div class="relative">
                <ComboboxInput
                    :name="name"
                    :class="classObject"
                    :display-value="(option) => option?.name"
                    data-spec-class="field-select-search__input"
                    :data-spec-value="selectedOption?.id"
                    :disabled="isDisabled"
                    autocomplete="new"
                    @focus="openOptionsIfClosed(open)"
                    @change="query = $event.target.value"
                    @blur="vuelidateField && vuelidateField.$touch()"
                />
                <ComboboxButton
                    ref="comboboxButtonRef"
                    class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-hidden"
                    data-spec-class="field-select-search__button"
                >
                    <ChevronUpDownIcon
                        class="h-5 w-5 text-tertiary"
                        aria-hidden="true"
                    />
                </ComboboxButton>
                <ComboboxOptions
                    v-if="filteredOptions.length > 0"
                    class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-surface--elevated py-1 text-base shadow-lg ring-1 focus:outline-hidden sm:text-sm"
                    data-spec-class="field-select-search__options"
                >
                    <ComboboxOption
                        v-for="option in filteredOptions"
                        :key="option.id"
                        v-slot="{ active, selected }"
                        :value="option"
                        as="template"
                    >
                        <li
                            :class="[
                                'relative cursor-default py-2 pr-9 pl-3 select-none',
                                active ? 'bg-brand text-gray-100' : 'text-primary',
                            ]"
                            data-spec-class="field-select-search__option"
                        >
                            <span :class="['block truncate', selected && 'font-semibold']">
                                {{ option.name }}
                            </span>

                            <span
                                v-if="selected"
                                :class="[
                                    'absolute inset-y-0 right-0 flex items-center pr-4',
                                    active ? 'text-gray-100' : 'text-brand',
                                ]"
                            >
                                <CheckIcon
                                    class="h-5 w-5"
                                    aria-hidden="true"
                                />
                            </span>
                        </li>
                    </ComboboxOption>
                </ComboboxOptions>
            </div>
        </Combobox>
    </BaseField>
</template>

<script setup lang="ts">
import { computed, ref, defineProps, withDefaults, defineEmits, watch } from 'vue';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/20/solid';
import {
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
} from '@headlessui/vue';
import { FormTheme, OptionItem, Theme } from '@/types';
import { useField } from '@/composables/field';
import BaseField from './BaseField.vue';

const { normalThemeFieldClasses, errorThemeFieldClasses, disabledThemeFieldClasses } = useField();

interface Props {
    label?: string;
    hint?: string;
    isOptional?: boolean;
    isDisabled?: boolean;
    vuelidateField?: unknown;
    options: OptionItem[];
    modelValue?: string;
    name: string;
    customThemeBase?: FormTheme;
    errorMessages?: string[];
}

const props = withDefaults(defineProps<Props>(), {
    label: '',
    hint: '',
    isOptional: false,
    isDisabled: false,
    modelValue: undefined,
    vuelidateField: undefined,
    customThemeBase: undefined,
    errorMessages: () => [],
});

const emit = defineEmits(['update:modelValue']);

const comboboxButtonRef = ref<{ $el: HTMLElement } | null>(null);

const openOptionsIfClosed = (isOpen: boolean) => {
    if (props.isDisabled || isOpen) {
        return;
    }

    const buttonEl = comboboxButtonRef.value?.$el;
    if (buttonEl instanceof HTMLElement) {
        buttonEl.click();
    }
};

const selectedOption = ref('undefined');

watch(selectedOption, async (newSelectedOption: OptionItem) => {
    emit('update:modelValue', newSelectedOption.id);
});

if (props.modelValue) {
    const foundOption = props.options.find((option: OptionItem) => option.id === props.modelValue);
    selectedOption.value = foundOption;
}

const query = ref('');
const filteredOptions = computed(() =>
    query.value === ''
        ? props.options
        : props.options.filter((option) =>
              option.name.toLowerCase().includes(query.value.toLowerCase())
          )
);

const vuelidateErrors = computed(() => {
    if (props.vuelidateField && props.vuelidateField.$errors.length) {
        return props.vuelidateField.$errors.map((error) => error.$message);
    }

    return [];
});

const errorMessagesTotal = computed(() => [...vuelidateErrors.value, ...props.errorMessages]);

const theme = computed(() => {
    if (props.isDisabled) {
        return Theme.Disabled;
    }
    if (errorMessagesTotal.value.length) {
        return Theme.Danger;
    }
    return Theme.Normal;
});

const themeBase = computed(() => {
    if (props.customThemeBase === FormTheme.Website) {
        return 'block mt-2 w-full rounded-sm p-4 text-dark';
    }

    return 'block w-full rounded-md shadow-xs sm:text-sm';
});

const classObject = computed(() => ({
    [themeBase.value]: true,
    [normalThemeFieldClasses]: theme.value === Theme.Normal,
    [errorThemeFieldClasses]: theme.value === Theme.Danger,
    [disabledThemeFieldClasses]: theme.value === Theme.Disabled,
}));
</script>
